/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { ref, onValue, set, update } from 'firebase/database';
import { database, progressAPI } from '../firebase';
import { buildRecentEntriesSummary } from '../lib/mentorContext';
import { useProgressTracking } from '../hooks/useProgressTracking';
import CalendarModal from './CalendarModal';
import ExecutionJournal from './ExecutionJournal';
import BusinessPlanFoundationManager from './BusinessPlanFoundationManager';
import '../styles/ActionPlanSidebar.css';

const ActionPlanIntegration = ({ 
  user, 
  userGoal, 
  businessPlan, 
  selectedDate, 
  onTaskComplete,
  foundationAPI: externalFoundationAPI,
  foundationStructure: externalFoundationStructure,
  isFoundationReady: externalIsFoundationReady,
  ...rest
}) => {
  const [completedTasks, setCompletedTasks] = useState({});
  const [showWeeklyBreakdown, setShowWeeklyBreakdown] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showExecutionJournal, setShowExecutionJournal] = useState(false);
  const [showJournal, setShowJournal] = useState(false);
  const [taskCompletions, setTaskCompletions] = useState({});
  const [foundationAPI, setFoundationAPI] = useState(externalFoundationAPI || null);
  const [foundationStructure, setFoundationStructure] = useState(externalFoundationStructure || null);
  const [isFoundationReady, setIsFoundationReady] = useState(externalIsFoundationReady || false);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [todaysPlan, setTodaysPlan] = useState(null);
  const [aiEnhancedActions, setAiEnhancedActions] = useState(null);
  const [isLoadingAIActions, setIsLoadingAIActions] = useState(false);
  const [lastAIGenerationDate, setLastAIGenerationDate] = useState(null);
  const [dailyJournalEntry, setDailyJournalEntry] = useState(null);
  const [internalSelectedDate, setInternalSelectedDate] = useState(selectedDate || new Date());
  const [progressData, setProgressData] = useState({
    overallProgress: 0,
    completedTasks: 0,
    totalTasks: 0,
    streakDays: 0,
    timeSpent: 0,
    daysSinceStart: 1,
    dailyGoalsMet: false,
    weeklyProgress: 0,
    weeklyGoalsMet: false,
    currentQuarter: 1,
    quarterProgress: 0
  });

  // Use progress tracking hook for AI-enhanced actions
  const {
    generateAIEnhancedDailyActions,
    progressData: hookProgressData,
    loading: progressLoading,
    refreshProgress
  } = useProgressTracking(user, userGoal, businessPlan);

  // Business Phase Display Helpers
  const getBusinessPhaseFromProgress = (overallProgress) => {
    if (overallProgress < 30) return 'Foundation';
    if (overallProgress < 70) return 'Growth';
    return 'Scale';
  };

  const getBusinessPhaseDisplay = (overallProgress, currentDay) => {
    const phase = getBusinessPhaseFromProgress(overallProgress);
    const phaseEmojis = {
      'Foundation': '🏗️',
      'Growth': '📈',
      'Scale': '🚀'
    };
    
    const phaseDays = {
      'Foundation': 'Days 1-30',
      'Growth': 'Days 31-70', 
      'Scale': 'Days 71-90'
    };
    
    return `${phaseEmojis[phase]} ${phase} Phase • ${phaseDays[phase]}`;
  };

  const getPhaseSpecificPlanTitle = (overallProgress) => {
    const phase = getBusinessPhaseFromProgress(overallProgress);
    const titles = {
      'Foundation': 'Foundation Building Plan',
      'Growth': 'Growth Acceleration Plan',
      'Scale': 'Scale Optimization Plan'
    };
    return titles[phase] || 'Today\'s Plan';
  };

  // Update when external props change
  useEffect(() => {
    console.log('ActionPlan: Props changed:', {
      hasExternalFoundationAPI: !!externalFoundationAPI,
      hasExternalFoundationStructure: !!externalFoundationStructure,
      externalIsFoundationReady: externalIsFoundationReady,
      hasUser: !!user,
      hasUserGoal: !!userGoal,
      hasBusinessPlan: !!businessPlan
    });
    
    if (externalFoundationAPI) setFoundationAPI(externalFoundationAPI);
    if (externalFoundationStructure) setFoundationStructure(externalFoundationStructure);
    if (externalIsFoundationReady !== undefined) setIsFoundationReady(externalIsFoundationReady);
  }, [externalFoundationAPI, externalFoundationStructure, externalIsFoundationReady]);

  // Add timeout for foundation loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isFoundationReady && user && userGoal) {
        console.warn('Foundation loading timeout - forcing creation with available data');
        // Force foundation creation with minimal data
        const minimalFoundation = {
          identity: {
            goalId: userGoal.id || 'temp-goal',
            businessPlanId: businessPlan?.id || 'temp-plan',
            goalTitle: userGoal.title || userGoal.goal || 'Your Goal',
            goalDescription: userGoal.description || '',
            version: '1.0'
          },
          timeline: {
            startDate: new Date().toISOString().split('T')[0],
            targetDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            totalDays: 90,
            totalWeeks: 13,
            totalQuarters: 4
          }
        };
        
        setFoundationStructure(minimalFoundation);
        setCurrentPeriod({
          quarter: 1,
          week: 1,
          day: 1,
          quarterProgress: 10,
          overallProgress: 1
        });
        
        setTodaysPlan({
          businessPhase: {
            title: 'Getting Started',
            subtitle: `Initial work on ${userGoal.title || 'your goal'}`,
            dailyCommitment: '2-4 hours'
          },
          day: {
            actions: {
              primary: `Begin work on ${userGoal.title || 'your goal'}`,
              secondary: ['Plan your approach', 'Set up workspace', 'Define next steps'],
              businessPhaseContext: 'Getting Started',
              dailyRoutineType: 'warmUp'
            },
            timeCommitment: '2-4 hours',
            priority: 'HIGH'
          },
          week: {
            focus: `Initial progress on ${userGoal.title || 'your goal'}`
          },
          currentQuarter: 1,
          currentWeek: 1
        });
        
        setIsFoundationReady(true);
      }
    }, 2000); // 2 second timeout

    return () => clearTimeout(timeout);
  }, [isFoundationReady, user, userGoal]);

  // Load completed tasks from Firebase
  useEffect(() => {
    if (!user) return;
    
    const loadCompletedTasks = async () => {
      try {
        const tasksRef = ref(database, `users/${user.uid}/completedTasks`);
        const unsubscribe = onValue(tasksRef, (snapshot) => {
          const data = snapshot.val() || {};
          setCompletedTasks(data);
        });
        return unsubscribe;
      } catch (error) {
        console.error('Error loading completed tasks:', error);
      }
    };

    loadCompletedTasks();
  }, [user]);

  // Load progress data
  useEffect(() => {
    if (!user || !foundationStructure) return;

    const progressRef = ref(database, `users/${user.uid}/progress`);
    const unsubscribe = onValue(progressRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setProgressData(data);
      } else if (foundationStructure) {
        // Initialize progress data based on foundation
        const daysSinceStart = calculateDaysSinceStart();
        const totalDays = foundationStructure.timeline.totalDays;
        const initialData = {
          overallProgress: Math.min(100, (daysSinceStart / totalDays) * 100),
          completedTasks: 0,
          totalTasks: 5,
          streakDays: Math.max(0, daysSinceStart - 1),
          timeSpent: 0,
          daysSinceStart: daysSinceStart,
          dailyGoalsMet: false,
          weeklyProgress: Math.min(100, ((daysSinceStart % 7) / 7) * 100),
          weeklyGoalsMet: false,
          currentQuarter: getCurrentQuarter(daysSinceStart, foundationStructure),
          quarterProgress: getQuarterProgress(daysSinceStart, foundationStructure)
        };
        setProgressData(initialData);
        set(progressRef, initialData);
      }
    });

    return () => unsubscribe();
  }, [user, foundationStructure]);

  // Update current period and today's plan when foundation is ready
  useEffect(() => {
    if (!foundationAPI || !foundationStructure) return;

    const daysSinceStart = calculateDaysSinceStart();
    const todaysFoundationPlan = foundationAPI.getTodaysFoundationPlan(daysSinceStart);
    
    if (todaysFoundationPlan) {
      setCurrentPeriod({
        quarter: todaysFoundationPlan.currentQuarter,
        week: todaysFoundationPlan.currentWeek,
        day: daysSinceStart,
        quarterProgress: getQuarterProgress(daysSinceStart, foundationStructure),
        pacePct: Math.min(100, (daysSinceStart / foundationStructure.timeline.totalDays) * 100)
      });

      setTodaysPlan(todaysFoundationPlan);
      setIsFoundationReady(true);
    }
  }, [foundationAPI, foundationStructure]);

  // Generate AI-enhanced actions when foundation is ready (only once per day)
  useEffect(() => {
    const generateAIActions = async () => {
      if (!isFoundationReady || !generateAIEnhancedDailyActions || isLoadingAIActions) return;

      const today = new Date().toISOString().split('T')[0];
      const goalId = userGoal?.id || 'default';

      // Don't regenerate if we already have AI actions for today
      if (lastAIGenerationDate === today && aiEnhancedActions) {
        console.log('AI actions already generated for today, skipping regeneration');
        return;
      }

      setIsLoadingAIActions(true);
      try {
        console.log('Generating AI-enhanced daily actions for', today);
        const journalContext = await buildRecentEntriesSummary(user.uid, { limit: 5 });
        const progressContext = {
          completedTasks: progressData.completedTasks,
          streakDays: progressData.streakDays
        };

        const aiActions = await generateAIEnhancedDailyActions(journalContext, progressContext);
        console.log('AI-enhanced actions generated:', aiActions);
        setAiEnhancedActions(aiActions);
        setLastAIGenerationDate(today);

        // Persist to RTDB so a fresh browser/device sees the same daily action.
        if (user && aiActions) {
          await set(ref(database, `users/${user.uid}/aiActions/${goalId}/${today}`), {
            actions: aiActions,
            generatedAt: Date.now(),
            date: today
          });
        }
      } catch (error) {
        console.error('Error generating AI actions:', error);
      } finally {
        setIsLoadingAIActions(false);
      }
    };

    generateAIActions();
  }, [isFoundationReady, generateAIEnhancedDailyActions]);

  // Load today's AI actions from RTDB (durable across browsers/devices).
  // Runs before generation so a hydrated action skips regeneration via the guard.
  useEffect(() => {
    if (!user) return;
    const goalId = userGoal?.id || 'default';
    const today = new Date().toISOString().split('T')[0];
    const unsubscribe = onValue(ref(database, `users/${user.uid}/aiActions/${goalId}/${today}`), (snap) => {
      const v = snap.val();
      if (v?.actions) {
        setAiEnhancedActions(v.actions);
        setLastAIGenerationDate(v.date);
      }
    });
    return unsubscribe;
  }, [user, userGoal]);

  // Load today's task completions from RTDB (durable across browsers/devices).
  useEffect(() => {
    if (!user) return;
    const goalId = userGoal?.id || 'default';
    const today = new Date().toISOString().split('T')[0];
    const unsubscribe = onValue(ref(database, `users/${user.uid}/taskCompletions/${goalId}/${today}`), (snap) => {
      setTaskCompletions(snap.val() || {});
    });
    return unsubscribe;
  }, [user, userGoal]);

  // Load daily journal entry
  useEffect(() => {
    if (!user) return;
    
    const today = new Date().toISOString().split('T')[0];
    const stored = localStorage.getItem(`dailyJournal_${user.uid}_${today}`);
    
    if (stored) {
      try {
        setDailyJournalEntry(JSON.parse(stored));
      } catch (error) {
        console.error('Error loading journal entry:', error);
      }
    }
  }, [user]);

  // Helper functions for foundation-based calculations
  const calculateDaysSinceStart = () => {
    if (!foundationStructure) return 1;
    const startDate = new Date(foundationStructure.timeline.startDate);
    const today = new Date();
    const diffTime = Math.abs(today - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(1, diffDays);
  };

  const getCurrentQuarter = (daysSinceStart, foundation) => {
    const daysPerQuarter = Math.floor(foundation.timeline.totalDays / 4);
    return Math.min(4, Math.ceil(daysSinceStart / daysPerQuarter));
  };

  const getQuarterProgress = (daysSinceStart, foundation) => {
    const daysPerQuarter = Math.floor(foundation.timeline.totalDays / 4);
    const currentQuarter = getCurrentQuarter(daysSinceStart, foundation);
    const daysIntoQuarter = ((daysSinceStart - 1) % daysPerQuarter) + 1;
    return Math.min(100, (daysIntoQuarter / daysPerQuarter) * 100);
  };

  const getCurrentDayOfWeek = () => {
    const daysSinceStart = calculateDaysSinceStart();
    return ((daysSinceStart - 1) % 7) + 1; // 1-7 (Mon-Sun)
  };

  const getCurrentDate = () => {
    const dateToUse = selectedDate ? new Date(selectedDate) : new Date();
    return dateToUse.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Get today's actions (AI-enhanced when available)
  const getTodaysActions = () => {
    // Use AI-enhanced actions if available
    if (aiEnhancedActions && aiEnhancedActions.baseAction) {
      return [{
        id: `ai-enhanced-${Date.now()}`,
        action: aiEnhancedActions.baseAction,
        priority: aiEnhancedActions.context.priority || 'HIGH',
        timeEstimate: aiEnhancedActions.context.estimatedHours || '4-6h',
        category: 'primary',
        successCriteria: aiEnhancedActions.context.successCriteria,
        skillsFocused: aiEnhancedActions.context.skillsFocused,
        measurable: true,
        isAIEnhanced: true
      }];
    }

    // Fallback to foundation plan if AI actions not available
    if (!todaysPlan || !todaysPlan.day) return [];

    const dayData = todaysPlan.day;
    const actions = [];

    // Primary action
    if (dayData.actions.primary) {
      actions.push({
        id: `foundation-primary-${dayData.globalDay}`,
        action: dayData.actions.primary,
        priority: dayData.priority || 'HIGH',
        timeEstimate: dayData.timeCommitment || '4-6h',
        category: 'primary',
        businessPhaseContext: dayData.actions.businessPhaseContext,
        dailyRoutineType: dayData.actions.dailyRoutineType,
        measurable: true,
        timeBlocks: dayData.actions.timeBlocks || [],
        isAIEnhanced: false
      });
    }

    // Secondary actions
    if (dayData.actions.secondary && dayData.actions.secondary.length > 0) {
      dayData.actions.secondary.forEach((secondaryAction, index) => {
        actions.push({
          id: `foundation-secondary-${dayData.globalDay}-${index}`,
          action: secondaryAction,
          priority: 'MEDIUM',
          timeEstimate: '30min-1h',
          category: 'secondary',
          businessPhaseContext: dayData.actions.businessPhaseContext,
          measurable: false,
          isAIEnhanced: false
        });
      });
    }

    return actions;
  };

  const handleTaskToggle = async (taskId, taskText, isCompleted) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const taskKey = `${today}_${taskId}`;
      
      if (isCompleted) {
        setCompletedTasks(prev => ({
          ...prev,
          [taskKey]: {
            task: taskText,
            completedAt: Date.now(),
            date: today,
            businessPhaseContext: todaysPlan?.businessPhase?.title || 'Current Phase'
          }
        }));
      } else {
        setCompletedTasks(prev => {
          const updated = { ...prev };
          delete updated[taskKey];
          return updated;
        });
      }

      // Update Firebase
      const tasksRef = ref(database, `users/${user.uid}/completedTasks`);
      if (isCompleted) {
        await update(tasksRef, {
          [taskKey]: {
            task: taskText,
            completedAt: Date.now(),
            date: today,
            businessPhaseContext: todaysPlan?.businessPhase?.title || 'Current Phase'
          }
        });
      } else {
        await update(tasksRef, { [taskKey]: null });
      }

      // Update progress tracking
      if (onTaskComplete) {
        onTaskComplete(taskId, isCompleted);
      }

      // Record in progress API — taskKey makes W11's latest-record-wins grouping exact
      await progressAPI.recordTaskCompletion(user.uid, userGoal.id, {
        taskId,
        description: taskText,
        completed: isCompleted,
        timestamp: Date.now(),
        date: today,
        businessPhase: todaysPlan?.businessPhase?.title,
        metadata: { taskKey: taskId }
      });

    } catch (error) {
      console.error('Error toggling task:', error);
    }
  };

  const isOldTaskCompleted = (taskId) => {
    const today = new Date().toISOString().split('T')[0];
    const taskKey = `${today}_${taskId}`;
    return !!completedTasks[taskKey];
  };

  const getProgressPercentage = () => {
    return progressData?.overallProgress || 0;
  };

  const getWeekProgressPercentage = () => {
    return progressData?.weeklyProgress || 0;
  };

  const getDailyProgressPercentage = () => {
    const todaysActions = getTodaysActions();
    if (todaysActions.length === 0) return 0;
    
    const completedCount = todaysActions.filter(action => 
      isTaskCompleted(action.id)
    ).length;
    
    return Math.round((completedCount / todaysActions.length) * 100);
  };

  const handlePhaseBreakdown = () => {
    setShowWeeklyBreakdown(!showWeeklyBreakdown);
  };

  const handleFoundationUpdate = (foundation) => {
    console.log('Foundation updated:', foundation?.identity);
    setFoundationStructure(foundation);
    setIsFoundationReady(!!foundation);
  };

  // Handle task completion
  const handleTaskCompletion = async (taskId, completed) => {
    const today = new Date().toISOString().split('T')[0];
    const goalId = userGoal?.id || 'default';
    const newCompletions = {
      ...taskCompletions,
      [taskId]: completed ? Date.now() : null
    };

    setTaskCompletions(newCompletions);

    // Persist to RTDB so completions survive a different browser/device.
    await set(ref(database, `users/${user.uid}/taskCompletions/${goalId}/${today}`), newCompletions);

    console.log(`Task ${taskId} marked as ${completed ? 'completed' : 'not completed'}`);
  };

  // Handle journal submission
  const handleJournalSubmit = (journalData) => {
    const today = new Date().toISOString().split('T')[0];
    const journalEntry = {
      ...journalData,
      date: today,
      timestamp: Date.now()
    };
    
    setDailyJournalEntry(journalEntry);
    
    // Save to localStorage
    localStorage.setItem(`dailyJournal_${user.uid}_${today}`, JSON.stringify(journalEntry));
    
    console.log('Journal entry saved:', journalEntry);
  };

  // Check if task is completed
  const isTaskCompleted = (taskId) => {
    return !!taskCompletions[taskId];
  };

  // Manual refresh handler (respects daily AI generation limit)
  const handleManualRefresh = async () => {
    const today = new Date().toISOString().split('T')[0];
    const goalId = userGoal?.id || 'default';

    // Check if we already generated AI actions today
    if (lastAIGenerationDate === today && aiEnhancedActions) {
      console.log('AI actions already generated today. Refresh will wait until tomorrow.');
      // Still refresh foundation data without regenerating AI
    setIsFoundationReady(false);
    setFoundationStructure(null);
    setCurrentPeriod(null);
    setTodaysPlan(null);
      setFoundationAPI(null);
      return;
    }
    
    console.log('Manual refresh triggered - regenerating AI actions for new day');
    
    // Reset AI actions and regenerate for new day
    setAiEnhancedActions(null);
    setIsLoadingAIActions(true);
    
    try {
      if (generateAIEnhancedDailyActions) {
        const journalContext = await buildRecentEntriesSummary(user.uid, { limit: 5 });
        const progressContext = {
          completedTasks: progressData.completedTasks,
          streakDays: progressData.streakDays
        };
        
        const aiActions = await generateAIEnhancedDailyActions(journalContext, progressContext);
        console.log('Refreshed AI-enhanced actions:', aiActions);
        setAiEnhancedActions(aiActions);
        setLastAIGenerationDate(today);
        
        // Persist the regenerated actions to the same durable RTDB node.
        if (user && aiActions) {
          await set(ref(database, `users/${user.uid}/aiActions/${goalId}/${today}`), {
            actions: aiActions,
            generatedAt: Date.now(),
            date: today
          });
        }
      }
    } catch (error) {
      console.error('Error refreshing AI actions:', error);
      // Reset foundation on error
      setIsFoundationReady(false);
      setFoundationStructure(null);
      setCurrentPeriod(null);
      setTodaysPlan(null);
    setFoundationAPI(null);
    } finally {
      setIsLoadingAIActions(false);
    }
  };

  // Example success criteria - this could come from props or be dynamic
  const successCriteria = "Completion of a comprehensive business strategy document outlining the mission, vision, target market, revenue streams, marketing tactics, and competitive analysis for the AI personal assistant software.";

  // Show loading state
  if (!isFoundationReady || !foundationStructure || !currentPeriod) {
    return (
      <BusinessPlanFoundationManager
        user={user}
        userGoal={userGoal}
        businessPlan={businessPlan}
        onFoundationUpdate={handleFoundationUpdate}
      >
        <div className="action-plan-sidebar-focused">
          <div className="action-plan-sidebar-header">
            <div className="progress-indicator">
              <div className="action-plan-current-date">{getCurrentDate()}</div>
              <div className="action-plan-day-counter">
                Day {currentPeriod?.day || calculateDaysSinceStart()} of {foundationStructure?.timeline?.totalDays || 90}
              </div>
            </div>
          </div>
          
          {!isFoundationReady ? (
            <div className="foundation-processing">
              <div className="processing-indicator">
                <div className="spinner"></div>
                <p>Analyzing your plan...</p>
              </div>
            </div>
          ) : (
            <>
              <div className="action-plan-focus-section">
                <h3>Today's Plan {isLoadingAIActions && <span style={{fontSize: '0.8rem', color: '#007AFF'}}>🤖 Generating...</span>}</h3>
                <div className="todays-goal-container">
                  {isLoadingAIActions ? (
                    <p className="todays-goal-sentence empty">
                      🤖 AI is generating your personalized daily action...
                    </p>
                  ) : getTodaysActions().length > 0 ? (
                    getTodaysActions().map(task => (
                      <div key={task.id}>
                        <p className="todays-goal-sentence">
                          {task.isAIEnhanced && <span style={{color: '#007AFF', fontSize: '0.8rem'}}>🤖 AI Enhanced: </span>}
                          {task.action}
                        </p>
                        {task.successCriteria && (
                          <p style={{fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)', marginTop: '0.5rem'}}>
                            ✅ Success: {task.successCriteria}
                          </p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="todays-goal-sentence empty">
                      Setting up your AI assistant...
                    </p>
                  )}
                </div>
              </div>
              
              <div className="progress-overview">
                <div className="progress-row">
                  <div className="progress-label-container">
                    <span className="progress-label">Daily</span>
                    <span className="progress-value">{getDailyProgressPercentage()}%</span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-fill daily" style={{ width: `${getDailyProgressPercentage()}%` }}></div>
                  </div>
                </div>
                <div className="progress-row">
                  <div className="progress-label-container">
                    <span className="progress-label">Weekly</span>
                    <span className="progress-value">{getWeekProgressPercentage()}%</span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-fill weekly" style={{ width: `${getWeekProgressPercentage()}%` }}></div>
                  </div>
            </div>
          </div>
          
          <div className="action-plan-quick-actions">
                <button className="action-button" onClick={() => setShowCalendar(true)}>
                  <span className="button-icon">📅</span> Calendar
              </button>
                <button className="action-button" onClick={() => setShowExecutionJournal(true)}>
                  <span className="button-icon">⚡</span> Execute
              </button>
                <button className="action-button">
                  <span className="button-icon">🤖</span> Coach
              </button>
                <button className="action-button" onClick={handleManualRefresh}>
                  <span className="button-icon">🔄</span> Refresh
              </button>
            </div>
              
              {dailyJournalEntry && (
                <div className="journal-status completed">
                  <span>📝</span> Daily journal completed
          </div>
              )}
            </>
          )}
          
          {showCalendar && (
            <CalendarModal 
              isOpen={showCalendar}
              onClose={() => setShowCalendar(false)}
              user={user}
              userGoal={userGoal}
              businessPlan={businessPlan}
              selectedDate={internalSelectedDate}
              onDateSelect={(date) => {
                setInternalSelectedDate(date);
                setShowCalendar(false);
              }}
            />
          )}
          
          {showExecutionJournal && (
            <ExecutionJournal 
              isOpen={showExecutionJournal}
              onClose={() => setShowExecutionJournal(false)}
              user={user}
              userGoal={userGoal}
              successCriteria={successCriteria}
              onDailyGoalComplete={(completionData) => {
                console.log('Daily goal completed:', completionData);
                // Refresh progress data to reflect completion
                if (refreshProgress) {
                  refreshProgress();
                }
                // Optionally close the journal after completion
                // setShowExecutionJournal(false);
              }}
            />
          )}
        </div>
      </BusinessPlanFoundationManager>
    );
  }

  const todaysActions = getTodaysActions();
  const currentDate = getCurrentDate();

  return (
    <BusinessPlanFoundationManager
      user={user}
      userGoal={userGoal}
      businessPlan={businessPlan}
      onFoundationUpdate={handleFoundationUpdate}
    >
      <div className="action-plan-sidebar-focused">
        <style>{`
          .goal-info {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }
          .business-phase-indicator {
            font-size: 12px;
            color: #64B5F6;
            font-weight: 500;
            opacity: 0.9;
          }
          .goal-badge {
            display: flex;
            align-items: flex-start;
            gap: 12px;
          }
          .goal-badge .goal-icon {
            margin-top: 2px;
          }
        `}</style>
        <div className="action-plan-sidebar-header">
          <div className="goal-badge">
            <span className="goal-icon">🎯</span>
            <div className="goal-info">
              <h2 className="goal-text">{userGoal?.title || 'Reach your goal'}</h2>
              <div className="business-phase-indicator">
                {getBusinessPhaseDisplay(progressData?.overallProgress || 0, calculateDaysSinceStart())}
              </div>
            </div>
          </div>
          <div className="progress-indicator">
          <div className="action-plan-current-date">{currentDate}</div>
            <div className="action-plan-day-counter">
              Day {currentPeriod?.day || calculateDaysSinceStart()} of {foundationStructure?.timeline?.totalDays || 90}
            </div>
          </div>
        </div>
        
        {!isFoundationReady ? (
          <div className="foundation-processing">
            <div className="processing-indicator">
              <div className="spinner"></div>
              <p>Analyzing your plan...</p>
            </div>
          </div>
        ) : (
          <>
        <div className="action-plan-focus-section">
              <h3>{getPhaseSpecificPlanTitle(progressData?.overallProgress || 0)} {isLoadingAIActions && <span style={{fontSize: '0.8rem', color: '#007AFF'}}>🤖 Generating...</span>}</h3>
              <div className="todays-goal-container">
                {isLoadingAIActions ? (
                  <p className="todays-goal-sentence empty">
                    🤖 AI is generating your personalized daily action...
                  </p>
                ) : getTodaysActions().length > 0 ? (
                  getTodaysActions().map(task => (
                    <div key={task.id} className="task-item">
                      <div className="task-content">
                        <p className="todays-goal-sentence">
                          {task.isAIEnhanced && <span style={{color: '#007AFF', fontSize: '0.8rem'}}>🤖 AI Enhanced: </span>}
                          {task.action}
                        </p>
                        {task.successCriteria && (
                          <p style={{fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)', marginTop: '0.5rem'}}>
                            ✅ Success: {task.successCriteria}
                          </p>
                        )}
                      </div>
                      <div className="task-actions">
                        <button 
                          className={`task-btn ${isTaskCompleted(task.id) ? 'completed' : 'incomplete'}`}
                          onClick={() => handleTaskCompletion(task.id, !isTaskCompleted(task.id))}
                        >
                          {isTaskCompleted(task.id) ? '✅ Done' : '⭕ Mark Done'}
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="todays-goal-sentence empty">
                    Setting up your AI assistant...
                  </p>
                )}
              </div>
            </div>
            
            <div className="progress-overview">
              <div className="progress-row">
                <div className="progress-label-container">
                  <span className="progress-label">Daily</span>
                  <span className="progress-value">{getDailyProgressPercentage()}%</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill daily" style={{ width: `${getDailyProgressPercentage()}%` }}></div>
                </div>
              </div>
              <div className="progress-row">
                <div className="progress-label-container">
                  <span className="progress-label">Weekly</span>
                  <span className="progress-value">{getWeekProgressPercentage()}%</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill weekly" style={{ width: `${getWeekProgressPercentage()}%` }}></div>
                </div>
          </div>
        </div>
        
        <div className="action-plan-quick-actions">
              <button className="action-button" onClick={() => setShowCalendar(true)}>
                <span className="button-icon">📅</span> Calendar
            </button>
              <button className="action-button" onClick={() => setShowExecutionJournal(true)}>
                <span className="button-icon">⚡</span> Execute
            </button>
              <button className="action-button">
                <span className="button-icon">🤖</span> Coach
            </button>
              <button className="action-button" onClick={handleManualRefresh}>
                <span className="button-icon">🔄</span> Refresh
            </button>
          </div>
          </>
        )}
        
        {showCalendar && (
          <CalendarModal 
            isOpen={showCalendar}
            onClose={() => setShowCalendar(false)}
            user={user}
            userGoal={userGoal}
            businessPlan={businessPlan}
            selectedDate={internalSelectedDate}
            onDateSelect={(date) => {
              setInternalSelectedDate(date);
              setShowCalendar(false);
            }}
          />
        )}
        
        {showExecutionJournal && (
          <ExecutionJournal 
            isOpen={showExecutionJournal}
            onClose={() => setShowExecutionJournal(false)}
            user={user}
            userGoal={userGoal}
            successCriteria={successCriteria}
            onDailyGoalComplete={(completionData) => {
              console.log('Daily goal completed:', completionData);
              // Refresh progress data to reflect completion
              if (refreshProgress) {
                refreshProgress();
              }
              // Optionally close the journal after completion
              // setShowExecutionJournal(false);
            }}
          />
        )}
      </div>
    </BusinessPlanFoundationManager>
  );
};

export default ActionPlanIntegration;
